import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  RotateCcw, 
  Save, 
  Lock,
  GitBranch,
  FileText,
  Calendar,
  AlertCircle,
  Loader,
  RefreshCw
} from 'lucide-react';
import versionHistoryService from '../../services/versionHistoryService';
import { Avatar } from '../ui';

const VersionHistory = forwardRef(({ surveyId, currentVersion, onRestoreVersion }, ref) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => {
    console.log('🔍 VersionHistory mounted with surveyId:', surveyId);
    if (surveyId) {
      fetchVersionHistory();
    } else {
      setError('No survey ID provided - check assessmentConfig.survey_id in ScreenBuilder');
      setLoading(false);
    }
  }, [surveyId]);

  const fetchVersionHistory = async () => {
    if (!surveyId) {
      setError('Survey ID is missing');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('Fetching version history for survey:', surveyId);
      const data = await versionHistoryService.getVersionHistory(surveyId);
      console.log('Version history data:', data);
      setVersions(data.results || data || []);
    } catch (err) {
      setError(err.message || 'Failed to load version history');
      console.error('Version history error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (version) => {
    setConfirmRestore(version);
  };

  const handleRestoreConfirm = async () => {
    if (!confirmRestore) return;

    setRestoring(true);
    try {
      const restoredVersion = await versionHistoryService.restoreVersion(surveyId, confirmRestore.id);
      await fetchVersionHistory();
      if (onRestoreVersion) {
        onRestoreVersion(restoredVersion);
      }
      setConfirmRestore(null);
    } catch (err) {
      setError('Failed to restore version');
      console.error(err);
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreCancel = () => {
    setConfirmRestore(null);
  };

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchVersionHistory
  }));

  // Version chip tone per snapshot type: auto gray, manual brand, publish green.
  const getVersionTypeColor = (type) => {
    switch (type) {
      case 'auto':
        return '';
      case 'manual':
        return 'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300';
      default:
        return 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400';
    }
  };

  const getVersionTypeIcon = (type) => {
    switch (type) {
      case 'auto':
        return <Clock className="h-3 w-3" />;
      case 'manual':
        return <Save className="h-3 w-3" />;
      case 'publish':
        return <Lock className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-gray-500 dark:text-gray-400">
        <Loader className="mb-3 h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        <p className="text-[13px]">Loading versions...</p>
        {surveyId && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Survey ID: {surveyId}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Debug Info */}
      {!surveyId && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3 dark:border-red-500/30 dark:bg-red-500/10">
          <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-semibold">Survey ID Missing</p>
              <p className="mt-1 opacity-80">assessmentConfig.survey_id is not set</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3 dark:border-red-500/30 dark:bg-red-500/10">
          <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-semibold">Error</p>
              <p className="mt-1 opacity-80">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Version Count */}
      <div className="border-b border-gray-100 px-4 py-2.5 dark:border-white/[0.06]">
        <p className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
          {versions.length} {versions.length === 1 ? 'version' : 'versions'} available
        </p>
      </div>

      {/* Version List */}
      <div className="flex-1 overflow-y-auto">
        {versions.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 py-8 text-center">
            <FileText className="mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" />
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">No version history yet</p>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
              Versions will appear as you work
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {versions.map((version) => (
              <motion.div
                key={version.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className={`px-4 py-3 transition-colors ${
                  version.version_number === currentVersion
                    ? 'bg-primary-50/40 dark:bg-primary-400/10'
                    : 'hover:bg-gray-50/80 dark:hover:bg-white/[0.03]'
                }`}
              >
                {/* Version Header */}
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`code-chip gap-1 ${getVersionTypeColor(version.version_type)}`}>
                      {getVersionTypeIcon(version.version_type)}
                      <span>v{version.version_number}</span>
                    </span>
                    {version.is_locked && <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />}
                  </div>
                  {version.version_number === currentVersion && (
                    <span className="badge badge-brand">
                      Current
                    </span>
                  )}
                </div>

                {/* Change Summary */}
                {version.change_summary && (
                  <p className="mb-2 line-clamp-2 text-[13px] text-gray-700 dark:text-gray-300">
                    {version.change_summary}
                  </p>
                )}

                {/* Metadata */}
                <div className="mb-2.5 flex flex-col gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span>{formatDate(version.created)}</span>
                  </div>
                  {version.created_by_name && (
                    <div className="flex items-center gap-1.5">
                      <Avatar name={version.created_by_name} size="xs" />
                      <span>{version.created_by_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span>{version.screen_count} screens, {version.element_count} elements</span>
                  </div>
                </div>

                {/* Restore Button */}
                {version.version_number !== currentVersion && (
                  <button
                    onClick={() => handleRestoreClick(version)}
                    disabled={restoring}
                    className="btn btn-secondary btn-sm w-full"
                  >
                    {restoring ? (
                      <Loader className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span>Restore</span>
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          Auto-saves every 5 minutes
        </p>
      </div>

      {/* Restore Confirmation Modal */}
      {confirmRestore && (
        <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-50">
                <AlertCircle className="h-4 w-4 text-warning-500 dark:text-warning-400" />
                Confirm Version Restore
              </h3>
            </div>
            <div className="px-6 py-5">
              <p className="mb-3 text-[13.5px] text-gray-600 dark:text-gray-300">
                Are you sure you want to restore <strong className="font-semibold text-gray-900 dark:text-gray-50">v{confirmRestore.version_number}</strong>?
              </p>
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                This will create a new version based on the selected snapshot.
              </p>
              {confirmRestore.change_summary && (
                <div className="mt-4 rounded-lg border border-gray-200/70 bg-gray-50 p-3.5 dark:border-white/[0.07] dark:bg-white/[0.03]">
                  <p className="mb-1 text-xs font-medium text-gray-800 dark:text-gray-100">Version Summary:</p>
                  <p className="text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-300">{confirmRestore.change_summary}</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <button
                onClick={handleRestoreCancel}
                disabled={restoring}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRestoreConfirm}
                disabled={restoring}
                className="btn btn-primary"
              >
                {restoring ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Restore Version
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default VersionHistory;
